import React, { useState, useEffect } from 'react';
import { FiPlus, FiRefreshCw, FiLayers } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { tasksAPI, batchAPI } from '../api';
import TaskList from './TaskList';

function Dashboard() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [batchJobs, setBatchJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewScrape, setShowNewScrape] = useState(false);
  const [scrapeUrl, setScrapeUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadTasks = async () => {
    try {
      const [tasksData, batchData] = await Promise.all([
        tasksAPI.getTasks(),
        batchAPI.listBatchJobs()
      ]);
      setTasks(tasksData);
      setBatchJobs(batchData);
    } catch (error) {
      console.error('Failed to load tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTasks();
    // Auto-refresh every 3 seconds
    const interval = setInterval(loadTasks, 3000);
    return () => clearInterval(interval);
  }, []);

  const handleNewScrape = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await tasksAPI.startScraping(scrapeUrl);
      setScrapeUrl('');
      setShowNewScrape(false);
      loadTasks();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to start scraping');
    } finally {
      setSubmitting(false);
    }
  };

  const scrapeTasks = tasks.filter(t => t.task_type === 'scrape');
  const translationTasks = tasks.filter(t => t.task_type === 'translation');

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <span className="spinner" style={{ width: '2rem', height: '2rem' }}></span>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h2>My Tasks</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button 
            className="btn btn-primary" 
            onClick={() => navigate('/batch')}
            style={{ backgroundColor: '#2196F3' }}
          >
            <FiLayers />
            Batch Workflow
          </button>
          <button className="btn btn-secondary" onClick={loadTasks}>
            <FiRefreshCw />
            Refresh
          </button>
        </div>
      </div>

      {/* Scraping Tasks */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Scraping Tasks</h3>
          <button 
            className="btn btn-primary"
            onClick={() => setShowNewScrape(!showNewScrape)}
          >
            <FiPlus />
            New Scrape
          </button>
        </div>
        
        {showNewScrape && (
          <form onSubmit={handleNewScrape} style={{ marginBottom: '1rem' }}>
            <div className="form-group">
              <label className="form-label" htmlFor="scrape-url">
                Amazon Product URL
              </label>
              <input
                id="scrape-url"
                type="url"
                className="form-input"
                value={scrapeUrl}
                onChange={(e) => setScrapeUrl(e.target.value)}
                placeholder="https://www.amazon.fr/dp/..."
                required
              />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span className="spinner"></span>
                    Starting...
                  </>
                ) : (
                  'Start Scraping'
                )}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowNewScrape(false);
                  setScrapeUrl('');
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
        
        <TaskList tasks={scrapeTasks} onUpdate={loadTasks} />
      </div>

      {/* Translation Tasks */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Translation Tasks</h3>
        </div>
        <TaskList tasks={translationTasks} onUpdate={loadTasks} showParent />
      </div>

      {/* Batch Jobs */}
      {batchJobs.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Batch Jobs</h3>
          </div>
          <div className="batch-jobs-list">
            {batchJobs.map(job => (
              <div 
                key={job.id} 
                className="batch-job-item"
                onClick={() => navigate(`/batch/${job.id}`)}
                style={{ 
                  cursor: 'pointer', 
                  padding: '1rem', 
                  borderBottom: '1px solid #e0e0e0',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h4 style={{ margin: '0 0 0.25rem 0' }}>{job.name}</h4>
                    <small style={{ color: '#666' }}>
                      Created: {new Date(job.created_at).toLocaleString()}
                    </small>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#2196F3' }}>
                      {job.stats?.completed || 0}/{job.stats?.total || 0}
                    </div>
                    <small style={{ color: '#666' }}>Tasks</small>
                  </div>
                </div>
                {job.stats?.failed > 0 && (
                  <div style={{ marginTop: '0.5rem', color: '#f44336', fontSize: '0.9rem' }}>
                    ⚠️ {job.stats.failed} failed task(s)
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard; 